import {
	editFrequenciesOptions,
	FrequencyStatusEnum,
	getEvaluationStatusesOptions,
	type EditFrequenciesCommand,
	type EvaluationCommandModel,
	type FullOrderViewModel,
	type RoutineFrequencyCommandModel,
	type RoutinePrnFrequencyCommandModel
} from '@generated';
import Yup from '~/util/yup';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useEvaluationsAndRoutineFrequencies } from '../queries';
import { EditFrequenciesContextProvider } from './EditFrequenciesContext';
import { useReducer } from 'react';
import { getInitialWizardState, wizardReducer } from './wizardReducer';
import { useIdentity } from '~/features/authentication/identity/IdentityContext';
import { EditFrequenciesToolbar } from './EditFrequenciesToolbar';
import { EditFrequenciesCalendar } from './EditFrequenciesCalendar';
import { EditFrequenciesList } from './EditFrequenciesList';
import { EditPrnFrequencies } from './EditPrnFrequencies';
import { getInvalidateFrequencyQueryKeys } from '../util';
import { useGetCertificationPeriodSchedule } from '~/features/scheduling/queries';
import { Modal } from '~/shared/components/modal/Modal';
import { useMutation } from '~/util/http/useMutation';
import { FooterButtonContainer } from '~/shared/forms/buttons/FooterButtonContainer';
import { CancelButton } from '~/shared/forms/buttons/CancelButton';
import { SubmitButton } from '~/shared/components/buttons/SubmitButton';
import { Banner } from '~/shared/components/Banner';
import { DisciplineChip } from '~/libraries/disciplines/DisciplineChip';
import { formatToShortDateAndTime } from '~/util/date';
import { useCadenceCorrelator } from '../useCadenceCorrelator';

const draftFrequencyOrdersValidationSchema = Yup.object().shape({
	draftEvaluations: Yup.array()
		.label('Evaluations')
		.of(
			Yup.object().shape({
				scheduledDateAndTime: Yup.date().label('Scheduled Date and Time').required(),
				disciplineId: Yup.number().label('Discipline').required(),
				assignedToId: Yup.string().label('Assigned To').nullable(),
				notes: Yup.string().label('Notes').nullable()
			})
		),
	draftRoutineFrequencies: Yup.array()
		.label('Routine Frequencies')
		.of(
			Yup.object().shape({
				frequencyCadenceTypeId: Yup.number().label('Frequency Cadence Type').required(),
				scheduledDateAndTime: Yup.date().label('Scheduled Date and Time').required(),
				disciplineId: Yup.number().label('Discipline').required(),
				assignedToId: Yup.string().label('Assigned To').nullable(),
				notes: Yup.string().label('Notes').nullable()
			})
		),
	draftPrnFrequencies: Yup.array()
		.label('PRN Frequencies')
		.of(
			Yup.object().shape({
				disciplineId: Yup.number().label('Discipline').required(),
				visitTypeId: Yup.number().label('Visit Type').required(),
				prnReason: Yup.string().label('PRN Reason').required(),
				count: Yup.number().label('Count').min(1, 'At least 1 visit is required').required()
			})
		)
});

interface EditFrequenciesFormModalProps {
	fullOrder: FullOrderViewModel;
	suggestedFrequencies?: {
		evaluations: EvaluationCommandModel[];
		routineFrequencies: RoutineFrequencyCommandModel[];
		prnFrequencies: RoutinePrnFrequencyCommandModel[];
	};
	closeModal: () => void;
}

export function EditFrequenciesFormModal({ fullOrder, closeModal, suggestedFrequencies }: EditFrequenciesFormModalProps) {
	const { refetch } = useSuspenseQuery(useEvaluationsAndRoutineFrequencies(fullOrder.id));

	const { isSameCadenceCorrelator } = useCadenceCorrelator();
	const { loggedInUser } = useIdentity();
	const { data: existingFrequencies } = useGetCertificationPeriodSchedule({
		certificationPeriod: fullOrder.certificationPeriod
	});

	const { data: evaluationStatuses } = useSuspenseQuery(getEvaluationStatusesOptions(fullOrder.certificationPeriodId));

	const editFrequencies = useMutation({
		...editFrequenciesOptions,
		invalidateQueryKeys: [
			getEvaluationStatusesOptions(fullOrder.certificationPeriodId).queryKey,
			...getInvalidateFrequencyQueryKeys(fullOrder.id)
		]
	});
	const [wizardState, dispatch] = useReducer(
		wizardReducer,
		getInitialWizardState(loggedInUser, evaluationStatuses, [...existingFrequencies], fullOrder, isSameCadenceCorrelator, suggestedFrequencies)
	);

	const submitButtonProps = suggestedFrequencies
		? {
				submitLabel: 'Apply Suggested Frequencies',
				submittingLabel: 'Applying Suggested Frequencies',
				requireDirtyToSubmit: false
		  }
		: undefined;

	const onSubmit = () => {
		const values: EditFrequenciesCommand = {
			orderId: fullOrder.id,
			draftEvaluations: wizardState.draftEvaluations,
			draftRoutineFrequencies: wizardState.draftRoutineFrequencies,
			draftPrnFrequencies: wizardState.draftPrnFrequencies,
			discontinuedFrequencies: wizardState.discontinuedFrequencies.map(f => ({ frequencyId: f.id })),
			discontinuedPrnFrequencyIds: wizardState.discontinuedPrnFrequencyIds,
			suggestedFrequenciesApplied: !!suggestedFrequencies
		};
		editFrequencies.mutate(values, {
			onSuccess: () => {
				refetch();
				closeModal();
			}
		});
	};

	const pendingMissedVisits = existingFrequencies.filter(x => x.frequencyStatusId === FrequencyStatusEnum.PendingMissedVisit);

	if (pendingMissedVisits.length > 0) {
		return (
			<Modal
				title={suggestedFrequencies ? 'Suggested Frequencies ' : 'Edit Frequencies'}
				closeModal={closeModal}
				size="xl"
				modalClassName="h-[92vh]"
				footer={
					<FooterButtonContainer>
						<CancelButton onCancel={closeModal} />
					</FooterButtonContainer>
				}
			>
				<Banner
					intent="warning"
					title="Pending missed visits must be resolved"
					message="Frequencies cannot be edited until all pending missed visits are resolved."
				>
					<ul className="divide-y divide-gray-200 rounded-md border border-gray-200 bg-white">
						{pendingMissedVisits.map(x => (
							<li key={x.id} className="flex items-center gap-4 px-4 py-3 text-sm text-slate-900">
								<DisciplineChip disciplineId={x.disciplineId} label={x.frequencyTypeLabel} />
								<span>{x.disciplineLabel}</span>
								<span className="text-slate-600">{x.assignedToName ?? 'Unassigned'}</span>
								<span className="ml-auto font-semibold">{formatToShortDateAndTime(x.scheduledDateAndTime)}</span>
							</li>
						))}
					</ul>
				</Banner>
			</Modal>
		);
	}

	return (
		<Modal
			title={suggestedFrequencies ? 'Suggested Frequencies ' : 'Edit Frequencies'}
			closeModal={closeModal}
			size="xl"
			modalClassName="h-[92vh]"
			footer={
				<FooterButtonContainer>
					<CancelButton onCancel={closeModal} />
					<SubmitButton
						onSubmit={onSubmit}
						{...submitButtonProps}
						isPending={editFrequencies.isPending}
						isDisabled={wizardState.frequencyDiscontinueError !== false}
					/>
				</FooterButtonContainer>
			}
		>
			<EditFrequenciesContextProvider wizardState={wizardState} dispatch={dispatch}>
				<div className="flex-1 space-y-6 overflow-y-auto">
					<EditFrequenciesToolbar />
					{wizardState.selectedFrequencyView === 'calendar' ? (
						<EditFrequenciesCalendar />
					) : wizardState.selectedFrequencyView === 'list' ? (
						<EditFrequenciesList />
					) : (
						<EditPrnFrequencies />
					)}
				</div>
			</EditFrequenciesContextProvider>
		</Modal>
	);
}
